/**
 * Magic-byte check for organization files (Business Account Pages slice 2, audit S2-2).
 * The declared type (browser / Storage Content-Type) is not trusted: the first bytes must match it.
 * Kept byte-for-byte identical to crm-frontend/src/utils/fileSniff.ts (client pre-check); the server copy is the
 * authority (verify-organization-file).
 */

export type SniffResult = { ok: true } | { ok: false; reason: string };

const OLE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
const ZIP = [0x50, 0x4b, 0x03, 0x04];

const startsWith = (b: Uint8Array, sig: number[], offset = 0): boolean =>
  b.length >= offset + sig.length && sig.every((x, i) => b[offset + i] === x);
const ascii = (s: string): number[] => [...s].map((c) => c.charCodeAt(0));

/** Markup that a browser could treat as HTML/SVG/script when served inline. Checked on text-like content only. */
const looksLikeMarkup = (text: string): boolean =>
  /<\s*(!doctype|html|head|body|script|svg|iframe|object|embed|meta|link|style|img|a)\b/i.test(
    text,
  ) || /^\s*<\?xml/i.test(text);

const isText = (b: Uint8Array): { ok: boolean; text: string } => {
  if (b.includes(0)) {
    return { ok: false, text: "" };
  }
  try {
    const text = new TextDecoder("utf-8", { fatal: true }).decode(b);
    return { ok: true, text };
  } catch {
    return { ok: false, text: "" };
  }
};

/**
 * `head` = the first bytes of the file (at least 4 KB when available; the whole file for text types up to 64 KB).
 */
export function sniffFile(head: Uint8Array, declaredType: string): SniffResult {
  const t = (declaredType || "").toLowerCase().split(";")[0].trim();
  const bad = (reason: string): SniffResult => ({ ok: false, reason });
  switch (t) {
    case "application/pdf":
      // %PDF- may be preceded by up to 1 KB of junk per the spec; accept only a near-start signature
      for (let i = 0; i <= Math.min(1024, head.length - 5); i++) {
        if (startsWith(head, ascii("%PDF-"), i)) {
          return { ok: true };
        }
      }
      return bad("not a PDF (no %PDF- signature)");
    case "image/png":
      return startsWith(head, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])
        ? { ok: true }
        : bad("not a PNG");
    case "image/jpeg":
      return startsWith(head, [0xff, 0xd8, 0xff])
        ? { ok: true }
        : bad("not a JPEG");
    case "image/gif":
      return startsWith(head, ascii("GIF87a")) ||
        startsWith(head, ascii("GIF89a"))
        ? { ok: true }
        : bad("not a GIF");
    case "image/webp":
      return startsWith(head, ascii("RIFF")) &&
        startsWith(head, ascii("WEBP"), 8)
        ? { ok: true }
        : bad("not a WebP image");
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      return startsWith(head, ZIP)
        ? { ok: true }
        : bad("not an Office (OOXML) file");
    case "application/msword":
    case "application/vnd.ms-excel":
    case "application/vnd.ms-powerpoint":
      return startsWith(head, OLE)
        ? { ok: true }
        : bad("not a legacy Office file");
    case "text/csv":
    case "text/plain": {
      const { ok, text } = isText(head);
      if (!ok) {
        return bad("not plain UTF-8 text");
      }
      if (looksLikeMarkup(text)) {
        return bad("text contains HTML/SVG/script markup");
      }
      return { ok: true };
    }
    default:
      return bad(`type ${t || "(none)"} is not allowed`);
  }
}

export const TEXT_TYPES = new Set(["text/csv", "text/plain"]);
/** How many leading bytes a caller should pass to sniffFile. */
export const sniffLength = (declaredType: string): number =>
  TEXT_TYPES.has((declaredType || "").toLowerCase()) ? 65536 : 4096;
